import { useEffect, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { useTranslation } from "react-i18next";
import { makeSettingsCardStyles, resolveTheme, TextField, type UiTheme } from "@jackedlogs/ui";
import { apiFetch, ApiError } from "./api";
import { changePassword, type AuthError } from "./auth/cognito";
import { useAuth } from "./auth/AuthContext";
import { useThemePalette } from "./theme";
import { useProfileDoc } from "./useProfileDoc";

// Settings → Account on the web console: email (read-only), display name,
// change password, data export, account deletion, sign out. Mirror of the
// mobile SettingsAccount. Name is a profile scalar (get-merge-PUT); password
// goes through Cognito; export downloads a JSON file; delete confirms via
// window.confirm then signs out.

export function AccountScreen() {
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const { t } = useTranslation();
  const { state, signOut } = useAuth();
  const { doc, saveField } = useProfileDoc();
  const card = makeSettingsCardStyles(theme);
  const styles = makeStyles(theme);

  const email = state.status === "signed_in" ? state.user.email : "—";

  return (
    <ScrollView style={styles.flex} contentContainerStyle={styles.body}>
      <View style={card.card}>
        <Text style={card.cardLabel}>
          {t("mobile.profile.labelEmail", { defaultValue: "EMAIL" })}
        </Text>
        <Text style={card.cardValue}>{email}</Text>
      </View>

      <NameEditor
        theme={theme}
        initial={(doc?.["name"] as string | undefined) ?? ""}
        onSave={(name) => saveField("name", name)}
      />

      <ChangePassword theme={theme} />

      <PrivacyActions theme={theme} onSignedOut={signOut} />

      <Pressable
        accessibilityRole="button"
        style={({ pressed }) => [card.secondaryAction, pressed && card.secondaryActionPressed]}
        onPress={signOut}
      >
        <Text style={card.secondaryActionText}>
          {t("mobile.profile.signOut", { defaultValue: "Sign out" })}
        </Text>
      </Pressable>
    </ScrollView>
  );
}

function NameEditor({
  theme,
  initial,
  onSave,
}: {
  theme: UiTheme;
  initial: string;
  onSave: (name: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const card = makeSettingsCardStyles(theme);
  const styles = makeStyles(theme);
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setValue(initial);
  }, [initial]);

  const trimmed = value.trim();
  const dirty = trimmed !== initial.trim();
  const valid = trimmed.length >= 1 && trimmed.length <= 80;

  const save = async () => {
    if (saving || !dirty || !valid) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await onSave(trimmed);
      setSaved(true);
    } catch (err) {
      setError(`api_error_${(err as ApiError).status ?? "unknown"}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={card.card}>
      <Text style={card.cardLabel}>
        {t("mobile.account.nameLabel", { defaultValue: "NAME" })}
      </Text>
      <View style={styles.fieldRow}>
        <TextField
          theme={theme}
          style={styles.input}
          value={value}
          onChangeText={(v) => {
            setValue(v);
            setSaved(false);
          }}
          placeholder={t("mobile.account.nameLabel", { defaultValue: "NAME" })}
          editable={!saving}
        />
        <Pressable
          accessibilityRole="button"
          disabled={!dirty || !valid || saving}
          onPress={() => void save()}
          style={({ pressed }) => [
            styles.inlineBtn,
            (!dirty || !valid || saving) && styles.inlineBtnDisabled,
            pressed && styles.inlineBtnPressed,
          ]}
        >
          {saving ? (
            <ActivityIndicator color={theme.palette.inkOnBrand} />
          ) : (
            <Text style={styles.inlineBtnText}>
              {t("mobile.common.save", { defaultValue: "Save" })}
            </Text>
          )}
        </Pressable>
      </View>
      {error ? <Text style={styles.error}>{error}</Text> : null}
      {saved ? <Text style={styles.ok}>{t("mobile.common.save", { defaultValue: "Save" })} ✓</Text> : null}
    </View>
  );
}

function ChangePassword({ theme }: { theme: UiTheme }) {
  const { t } = useTranslation();
  const card = makeSettingsCardStyles(theme);
  const styles = makeStyles(theme);
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const valid = oldPw.length > 0 && newPw.length >= 10 && newPw === confirmPw;

  const save = async () => {
    if (saving || !valid) return;
    setSaving(true);
    setError(null);
    setDone(false);
    try {
      await changePassword(oldPw, newPw);
      setOldPw("");
      setNewPw("");
      setConfirmPw("");
      setDone(true);
    } catch (err) {
      setError((err as AuthError).message ?? "Change failed");
    } finally {
      setSaving(false);
    }
  };

  const field = (
    value: string,
    setter: (v: string) => void,
    placeholder: string,
    autoComplete: "current-password" | "new-password",
  ) => (
    <TextField
      theme={theme}
      style={styles.pwInput}
      value={value}
      onChangeText={(v) => {
        setter(v);
        setDone(false);
      }}
      secureTextEntry
      autoCapitalize="none"
      autoCorrect={false}
      autoComplete={autoComplete}
      placeholder={placeholder}
      editable={!saving}
    />
  );

  return (
    <View style={card.card}>
      <Text style={card.cardLabel}>
        {t("mobile.account.passwordLabel", { defaultValue: "CHANGE PASSWORD" })}
      </Text>
      {field(oldPw, setOldPw, t("mobile.account.currentPassword", { defaultValue: "Current password" }), "current-password")}
      {field(newPw, setNewPw, t("mobile.account.newPassword", { defaultValue: "New password" }), "new-password")}
      {field(confirmPw, setConfirmPw, t("mobile.account.confirmPassword", { defaultValue: "Confirm new password" }), "new-password")}
      {error ? <Text style={styles.error}>{error}</Text> : null}
      {done ? <Text style={styles.ok}>{t("mobile.account.passwordChanged", { defaultValue: "Password changed." })}</Text> : null}
      <Pressable
        accessibilityRole="button"
        disabled={!valid || saving}
        onPress={() => void save()}
        style={({ pressed }) => [
          card.secondaryAction,
          (!valid || saving) && card.disabled,
          pressed && card.secondaryActionPressed,
        ]}
      >
        {saving ? (
          <ActivityIndicator color={theme.palette.ink} />
        ) : (
          <Text style={card.secondaryActionText}>
            {t("mobile.account.passwordLabel", { defaultValue: "CHANGE PASSWORD" })}
          </Text>
        )}
      </Pressable>
    </View>
  );
}

function PrivacyActions({
  theme,
  onSignedOut,
}: {
  theme: UiTheme;
  onSignedOut: () => void;
}) {
  const { t } = useTranslation();
  const card = makeSettingsCardStyles(theme);
  const styles = makeStyles(theme);
  const [busy, setBusy] = useState<"export" | "delete" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onExport = async () => {
    if (busy) return;
    setBusy("export");
    setError(null);
    try {
      const data = await apiFetch<unknown>("/api/me/export");
      const date = new Date().toISOString().slice(0, 10);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `jackedlogs-export-${date}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(`api_error_${(err as ApiError).status ?? "unknown"}`);
    } finally {
      setBusy(null);
    }
  };

  const onDelete = async () => {
    if (busy) return;
    const ok =
      typeof window !== "undefined" &&
      window.confirm(
        `${t("mobile.profile.deleteAlertTitle", { defaultValue: "Delete account" })}\n\n${t(
          "mobile.profile.deleteAlertBody",
          {
            defaultValue:
              "Your account, sessions, plans, programs and maxes will be permanently deleted. Deletions are not recoverable.",
          },
        )}`,
      );
    if (!ok) return;
    setBusy("delete");
    setError(null);
    try {
      await apiFetch<unknown>("/api/me", { method: "DELETE" });
      onSignedOut();
    } catch (err) {
      setError(`api_error_${(err as ApiError).status ?? "unknown"}`);
      setBusy(null);
    }
  };

  return (
    <View style={card.card}>
      <Text style={card.cardLabel}>
        {t("mobile.profile.labelPrivacy", { defaultValue: "PRIVACY" })}
      </Text>
      <Text style={card.cardHelp}>
        {t("mobile.profile.privacyHelp", {
          defaultValue:
            "Export everything as JSON or delete the account permanently.",
        })}
      </Text>
      {error ? <Text style={styles.error}>{error}</Text> : null}
      <Pressable
        accessibilityRole="button"
        disabled={busy !== null}
        onPress={() => void onExport()}
        style={({ pressed }) => [
          card.secondaryAction,
          busy !== null && card.disabled,
          pressed && card.secondaryActionPressed,
        ]}
      >
        {busy === "export" ? (
          <ActivityIndicator color={theme.palette.ink} />
        ) : (
          <Text style={card.secondaryActionText}>
            {t("mobile.profile.exportButton", { defaultValue: "Export my data" })}
          </Text>
        )}
      </Pressable>
      <Pressable
        accessibilityRole="button"
        disabled={busy !== null}
        onPress={() => void onDelete()}
        style={({ pressed }) => [
          card.dangerAction,
          busy !== null && card.disabled,
          pressed && card.dangerActionPressed,
        ]}
      >
        {busy === "delete" ? (
          <ActivityIndicator color={theme.palette.danger} />
        ) : (
          <Text style={card.dangerActionText}>
            {t("mobile.profile.deleteButton", { defaultValue: "Delete my account" })}
          </Text>
        )}
      </Pressable>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: p.bg },
    body: { paddingHorizontal: s.s5, paddingVertical: s.s4, gap: s.s4 },
    fieldRow: { flexDirection: "row", alignItems: "center", gap: s.s3 },
    // Layout only — box internals live in the <TextField> primitive.
    input: {
      flex: 1,
    },
    // Layout only — box internals live in the <TextField> primitive.
    pwInput: {
      marginTop: s.s2,
    },
    inlineBtn: {
      minHeight: 40,
      paddingHorizontal: s.s4,
      borderRadius: r.r3,
      backgroundColor: p.brand500,
      alignItems: "center",
      justifyContent: "center",
    },
    inlineBtnDisabled: { opacity: 0.4 },
    inlineBtnPressed: { opacity: 0.8 },
    inlineBtnText: { color: p.inkOnBrand, fontSize: t.sizes.base, fontWeight: "600" },
    error: { color: p.danger, fontSize: t.sizes.base },
    ok: { color: p.complete, fontSize: t.sizes.sm, fontWeight: "600" },
  });
}
